import { getThousandsSeparator } from './patchedToLocaleString.helpers.js';
// patchedToLocaleString.js
// this is a wrapper around Number.toLocaleString that polyfills a subset of the
// finalized Intl.NumberFormat v3 specification.
//
// You can view the ful v3 spec at:
// https://github.com/tc39/proposal-intl-numberformat-v3

// *****************************************************************************
// roundingMode: 'floor' patch
//
// You can see a detailed description of the options.roundingMode behavior we
// are polyfilling here:
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#rounding_modes
//
// And you can access a browser compatibility table to see how adoption is
// progressing here:
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#browser_compatibility
//
// This patch can be deleted when roundingMode: 'floor' is supported in all
// major browsers.
const patchRoundingModeFloor = {
  test: (number, locale, options) =>
    options.roundingMode === 'floor' &&
    options.maximumFractionDigits !== undefined,
  patchInputNumber: (number, locale, options) =>
    Math.floor(number * 10 ** options.maximumFractionDigits) /
    10 ** options.maximumFractionDigits
};

// *****************************************************************************
// trailingZeroDisplay: 'stripIfInteger' patch
//
// this polyfills options.trailingZeroDisplay supporting the 'stripIfInteger'
// option, and you can see a description for this behavior here:
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#trailingzerodisplay
//
// This patch can be deleted when trailingZeroDisplay: 'stripIfInteger' is
// supported in all major browsers.
const patchTrailingZeroDisplay = {
  test: (number, locale, options) =>
    options.trailingZeroDisplay === 'stripIfInteger',
  patchOutputString: (output) => output.replace(/[,.]0+/, '')
};

// *****************************************************************************
// signDisplay: 'negative' patch
//
// this polyfills options.signDisplay supporting the 'negative' option. This
// behavior is the same as signDisplay: "auto" except that it does not display
// a negative for negative 0. You can see a description for this behavior here:
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#signdisplay
//
// this patch can be deleted when signDisplay: 'negative' is supported in all
// major browsers.
const patchSignDisplayNegative = {
  test: (number, locale, options) => options.signDisplay === 'negative',
  patchOptions: (number, locale, options) => ({
    ...options,
    signDisplay: 'exceptZero'
  }),
  patchOutputString: (output) => output.replace('+', '')
};

// *****************************************************************************
// es-ES & child locales thousands separator patch
//
// Currently (september 2022), the Unicode CLDR data for es-ES and locales that
// inherit from es-ES does not use a thousands separator for 4-digit numbers.
// This is in-line with the Diccionario panhispánico de dudas style guide from
// the Spanish Royal Academy but it does *not* line up with any common language
// usage including major periodicals and government publication.
//
// The CLDR data may soon be updated to reflect the common usage, but it will
// take a while for browser vendors to pick up these changes. In the mean time,
// we can patch this locally by specifying useGrouping: 'always' for affected
// locales.
//
// This patch can be deleted when (5000).toLocaleString('es-ES') returns
// "5.000" in all major browsers.
const patchSpanishThousandsBehavior = {
  test: (number, locale) => ['es-ES', 'pt-PT'].includes(locale),
  patchOutputString: (output, locale) => {
    const thousandsSeparator = getThousandsSeparator(locale);
    return output.replace(
      /^\d{4}/,
      (match) => `${match[0]}${thousandsSeparator}${match.substring(1)}`
    );
  }
};

// *****************************************************************************
// patch an uncommon Safari 14.1 issue with various NumberFormat options
//
// This is based on the investigation and fix that Google shipped when they
// encountered this same issue:
//
// Discussion:
// https://github.com/google/site-kit-wp/issues/3255
//
// Their fix:
// https://github.com/google/site-kit-wp/pull/3620/files#diff-43329dbb491c976dcedecffe512e85e5e1cc9afa594a82fd72d6fc29ccc0da1bR308-R318

const shouldUseSafari14Patch = (() => {
  // here we do a "sniff test" to see if this browser misbehaves in the specific
  // way that would be fixed by our Safari 14.x patch below.
  const knownGoodUnitOptions = {
    style: 'unit',
    unit: 'kilometer'
  };
  const knownGoodSignDisplayOptions = {
    signDisplay: 'exceptZero'
  };
  const knownGoodCurrencyOptions = {
    style: 'currency',
    currency: 'USD',
    currencyDisplay: 'narrowSymbol',
    currencySign: 'accounting'
  };
  try {
    (123).toLocaleString('en-US', knownGoodUnitOptions);
    (123).toLocaleString('en-US', knownGoodSignDisplayOptions);
    (123).toLocaleString('en-US', knownGoodCurrencyOptions);
    // if all these checks pass, return false to indicate that we should not
    // activate the Safari 14 patch.
    return false;
  } catch (e) {
    console.error('DETECTED INCOMPLETE BROWSER SUPPORT FOR Intl.NumberFormat:');
    console.error(e);
    console.warn('ENABLING FALLBACK BEHAVIOR');
    // Now we do something kind of terrible. We want to report this error to
    // Sentry, but we don't have any way of safely accessing the Sentry
    // context of the consuming application here. Sooooo instead we throw
    // it as an un-caught error in a setTimeout, which will be caught and
    // reported by Sentry if there's a Sentry instance on the page, but won't
    // blow up any code consuming this module.
    setTimeout(() => {
      throw new Error(
        'shouldUseSafari14Patch: detected incomplete browser support for Intl.NumberFormat'
      );
    }, 0);
    // since one of the .toLocaleString() calls above failed, return true to
    // indicate that the Safari 14 patch should be activated.
    return true;
  }
})();

const safari14SafeToLocaleString = (number, locale, patchedOptions) => {
  // this function is a transparent passthru UNLESS we've decided we're in
  // a non-trustworthy browser context:
  if (!shouldUseSafari14Patch) {
    return number.toLocaleString(locale, patchedOptions);
  }
  // if we get here then it means we don't trust this browser and we're going
  // try to thread the needle in terms of its support for Intl.NumberFormat
  // options:
  try {
    return number.toLocaleString(locale, patchedOptions);
  } catch (e) {
    console.error(
      `caught toLocaleString error: ${JSON.stringify({
        number,
        locale,
        patchedOptions
      })}`
    );
    console.error(e.message);
    // Remove these keys from formatOptions when the given value is set:
    const unstableFormatOptionValues = {
      currencyDisplay: 'narrowSymbol',
      currencySign: 'accounting',
      style: 'unit'
    };

    // Remove these keys from formatOptions regardless of value.
    const unstableFormatOptions = ['signDisplay', 'compactDisplay'];

    const safeOptions = Object.fromEntries(
      Object.entries(patchedOptions).filter(([key, value]) => {
        switch (true) {
          case unstableFormatOptions.includes(key):
            return false;
          case key in unstableFormatOptionValues &&
            unstableFormatOptionValues[key] === value:
            return false;
          default:
            return true;
        }
      })
    );

    return number.toLocaleString(locale, safeOptions);
  }
};

// *****************************************************************************
// 'noDecimals' syntactic sugar
//
// This implements a noDecimals: boolean syntactic sugar implementation that
// spreads the options { minimumFractionDigits: 0, maximumFractionDigits: 0 }
// into the formatter options when true.

// Note: it steamrolls any values set for minimumFractionDigits and/or
// maximumFractionDigits, setting them both to 0 regardless.

const patchNoDecimals = {
  test: (number, locale, options) => Boolean(options.noDecimals),
  patchOptions: (number, locale, options) => ({
    ...options,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })
};

const allPatches = [
  patchRoundingModeFloor,
  patchTrailingZeroDisplay,
  patchSignDisplayNegative,
  patchSpanishThousandsBehavior,
  patchNoDecimals
];

const patchedToLocaleString = (number, locale, options = {}) => {
  const patchesToApply = allPatches.filter((patch) =>
    patch.test(number, locale, options)
  );

  const patchedOptions = patchesToApply
    .filter((patch) => patch.patchOptions)
    .reduce((opts, patch) => patch.patchOptions(number, locale, opts), options);

  const patchedNumber = patchesToApply
    .filter((patch) => patch.patchInputNumber)
    .reduce(
      (num, patch) => patch.patchInputNumber(num, locale, patchedOptions),
      number
    );

  const outputString = safari14SafeToLocaleString(
    patchedNumber,
    locale,
    patchedOptions
  );

  const patchedOutput = patchesToApply
    .filter((patch) => patch.patchOutputString)
    .reduce(
      (string, patch) =>
        patch.patchOutputString(string, locale, patchedOptions),
      outputString
    );

  return patchedOutput;
};

export default patchedToLocaleString;
